/** Variables */

import { isNumber, twoDigits } from "./number.tools";
import { objectKeys } from "./object.tools";
import { DAYS, HOURS, MILLISECONDS, MINUTES, MONTHS, SECONDS, TIME_IN_MINUTES, TIME_IN_SECONDS } from "./type.tools";

/** Default separator for the different format */
export const DEFAULT_HHMM_SEPARATOR: string = ":";

/** Number of minutes in one hour */
export const MINUTES_IN_ONE_HOUR: MINUTES = 60;
/** Number of seconds in one minute */
export const SECONDS_IN_ONE_MINUTE: SECONDS = 60;

/** Number of seconds in one hour */
export const SECONDS_IN_ONE_HOUR: SECONDS = MINUTES_IN_ONE_HOUR * SECONDS_IN_ONE_MINUTE;

/** Number of hours in one day */
export const HOURS_IN_ONE_DAY: HOURS = 24;

/** Number of of minutes in one day */
export const MINUTES_IN_ONE_DAY: MINUTES = MINUTES_IN_ONE_HOUR * HOURS_IN_ONE_DAY;

/** Number of seconds in one day */
export const SECONDS_IN_ONE_DAY: SECONDS = MINUTES_IN_ONE_DAY * SECONDS_IN_ONE_MINUTE;

/** Number of seconds in one year, non leap year  */
export const SECONDS_IN_ONE_YEAR: SECONDS = SECONDS_IN_ONE_DAY * 365;

/** Number of seconds in one year, leap year  */
export const SECONDS_IN_ONE_LEAP_YEAR: SECONDS = SECONDS_IN_ONE_DAY * 366;

/** Number of millis in one second */
export const MILLIS_IN_ONE_SECOND: MILLISECONDS = 1000;

/** Number of millis in one minute */
export const MILLIS_IN_ONE_MINUTE: MILLISECONDS = MILLIS_IN_ONE_SECOND * SECONDS_IN_ONE_MINUTE;

/** Number of millis in one hour */
export const MILLIS_IN_ONE_HOUR: MILLISECONDS = MILLIS_IN_ONE_MINUTE * MINUTES_IN_ONE_HOUR;

/** Number of millis in one day */
export const MILLIS_IN_ONE_DAY: MILLISECONDS = MILLIS_IN_ONE_HOUR * HOURS_IN_ONE_DAY;

/** Number of days in one week */
export const DAYS_IN_ONE_WEEK: DAYS = 7;

/** Number of hours in one week */
export const HOURS_IN_ONE_WEEK: HOURS = HOURS_IN_ONE_DAY * DAYS_IN_ONE_WEEK;

/** Number of minutes in one week */
export const MINUTES_IN_ONE_WEEK: MINUTES = MINUTES_IN_ONE_DAY * DAYS_IN_ONE_WEEK;

/** Number of seconds in one week */
export const SECONDS_IN_ONE_WEEK: SECONDS = SECONDS_IN_ONE_DAY * DAYS_IN_ONE_WEEK;

/** Number of millis in one week */
export const MILLIS_IN_ONE_WEEK: MILLISECONDS = MILLIS_IN_ONE_DAY * DAYS_IN_ONE_WEEK;

/** Number of months in one year */
export const MONTHS_IN_ONE_YEAR: MONTHS = 12;

/**
 * Convert seconds(s) to minutes(min)
 */
export function secondsToMinutes(seconds: SECONDS): MINUTES {
    return seconds / SECONDS_IN_ONE_MINUTE;
}

/**
 * Convert seconds(s) to hours(h)
 */
export function secondsToHours(seconds: SECONDS): HOURS {
    return seconds / SECONDS_IN_ONE_HOUR;
}

/**
 * Convert minutes(min) to seconds(s)
 */
export function minutesToSeconds(minutes: MINUTES): SECONDS {
    return minutes * SECONDS_IN_ONE_MINUTE;
}

/**
 * Convert hours(h) to seconds(s)
 */
export function hoursToSeconds(hours: HOURS): SECONDS {
    return hours * SECONDS_IN_ONE_HOUR;
}

/**
 * Convert minutes(s) to hours(h)
 */
export function minutesToHours(minutes: MINUTES): HOURS {
    return minutes / MINUTES_IN_ONE_HOUR;
}

/**
 * Convert hours(h) to minutes(min)
 */
export function hoursToMinutes(hours: HOURS): MINUTES {
    return hours * MINUTES_IN_ONE_HOUR;
}

/**
 * Convert days(d) to minutes(min)
 */
export function daysToMinutes(days: DAYS): MINUTES {
    return days * MINUTES_IN_ONE_DAY;
}

/**
 * Convert minutes(min) to days(d)
 */
export function minutesToDays(minutes: MINUTES): DAYS {
    return minutes / MINUTES_IN_ONE_DAY;
}

// TODO getFirstDurationUnit Return the first/biggest unit defined from a duration 

/** Get days/hours/minutes values from a value in minutes */
export function convertTimeInMinutesToDaysHoursMinutes(timeInMinutes: TIME_IN_MINUTES): {
    days: DAYS,
    hours: HOURS,
    minutes: MINUTES
} {
    return convertTimeInSecondsToDaysHoursMinutesSeconds(timeInMinutes * SECONDS_IN_ONE_MINUTE);
}

/** Get days/hours/minutes/seconds values from a value in seconds */
export function convertTimeInSecondsToDaysHoursMinutesSeconds(timeInSeconds: TIME_IN_SECONDS): {
    days: DAYS,
    hours: HOURS,
    minutes: MINUTES,
    seconds: SECONDS
} {
    return {
        days: Math.floor(timeInSeconds / SECONDS_IN_ONE_DAY),
        hours: Math.floor(timeInSeconds % SECONDS_IN_ONE_DAY / SECONDS_IN_ONE_HOUR),
        minutes: Math.floor(timeInSeconds % SECONDS_IN_ONE_HOUR / SECONDS_IN_ONE_MINUTE),
        seconds: Math.floor(timeInSeconds % SECONDS_IN_ONE_MINUTE)
    };
}

/**
 * Formats a number representing a number of minutes into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * formatMinutesHHMM(120) => "02:00", formatMinutesHHMM(-120) => "-02:00"
 * @param minutes to convert
 * @param p options (@see  FormatHHMMOptions )
 */
export function formatMinutesHHMM(minutes: MINUTES, p?: FormatHHMMOptions): string {
    if (minutes < 0) { return "-" + formatMinutesHHMM(-minutes, p); }

    const convertedTime = convertTimeInMinutesToDaysHoursMinutes(minutes);
    const hour = convertedTime.hours + (p?.dayFormat ? 0 : convertedTime.days * HOURS_IN_ONE_DAY);
    const separator = p?.separator ?? DEFAULT_HHMM_SEPARATOR;
    return (p?.singleDigitHour ? hour : twoDigits(hour)) + separator + twoDigits(convertedTime.minutes);
}

/**
 * Formats a number representing a number of seconds into hour:minutes:seconds string ensuring hour, minutes and seconds have at least 2 digits
 * formatSecondsHHMMSS(3600) => "01:00:00", formatSecondsHHMMSS(-3600) => "-01:00"
 * @param seconds to convert
 * @param p options (@see FormatHHMMOptions  )
 */
export function formatSecondsHHMMSS(seconds: SECONDS, p?: FormatHHMMOptions): string {
    if (seconds < 0) { return "-" + formatSecondsHHMMSS(-seconds, p); }

    const convertedTime = convertTimeInSecondsToDaysHoursMinutesSeconds(seconds);
    const hour = convertedTime.hours + (p?.dayFormat ? 0 : convertedTime.days * HOURS_IN_ONE_DAY);
    const sep = p?.separator != null ? p.separator : DEFAULT_HHMM_SEPARATOR;
    return (p?.singleDigitHour ? hour : twoDigits(hour)) + sep + twoDigits(convertedTime.minutes) + sep + twoDigits(convertedTime.seconds);
}

/**
 * Parse a string representing hours and minutesInHour (HH:MM) into the corresponding number of minutes.
 * parseMinutesHHMM("02:00") => 120, parseMinutesHHMM("-02:00") => -120
 * Handle only hours as well : parseMinutesHHMM("01") => 60
 * @param time hour:minutes string
 * @param options
 * @param options.strict Parse only valid string => will throw an error if the string has a negative factor and/or if HH>=24 or mm>=60
 * @returns number of minutes
 */
export function parseMinutesHHMM(time: string, options?: { strict?: boolean }): number {
    let negativeFactor = 1;
    if (time[0] === "-") {
        time = time.substring(1);
        negativeFactor = -1;
    }
    const timeStrSplit = time.split(":");
    const hours = parseInt(timeStrSplit[0]);
    if (options?.strict && hours >= HOURS_IN_ONE_DAY) {
        throw new Error(`Invalid count of hours : ${hours} >= ${HOURS_IN_ONE_DAY}`);
    }
    let minutes = 0;
    if (timeStrSplit[1] != null) {
        minutes = parseInt(timeStrSplit[1]);
        if (options?.strict && minutes >= MINUTES_IN_ONE_HOUR) {
            throw new Error(`Invalid count of minutes : ${minutes} >= ${MINUTES_IN_ONE_HOUR}`);
        }
    }
    if (!isNumber(hours) || !isNumber(minutes)) {
        throw new Error("Unable to parse input time : " + time);
    }

    return negativeFactor * (hours * MINUTES_IN_ONE_HOUR + minutes);
}

/**
 * Parse a string representing hours and minutesInHour (HH:MM) into the corresponding number of seconds.
 * parseSecondsHHMM("02:00") => 7200, parseSecondsHHMM("-02:00") => -7200
 * Handle only hours as well : parseSecondsHHMM("01") => 3600
 * @param time hour:minutes string
 * @param options
 * @param options.strict Parse only valid string => will throw an error if the string has a negative factor and/or if HH>=24 or mm>=60
 * @returns number of seconds
 */
export function parseSecondsHHMM(time: string, options?: { strict?: boolean }): TIME_IN_SECONDS {
    return minutesToSeconds(parseMinutesHHMM(time, options));
}

/**
 * Formats a number representing a number of seconds into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * formatSecondsHHMM(120) => "00:02", formatSecondsHHMM(50) => "00:00", formatSecondsHHMM(-120) => "-00:02"
 * @param seconds to convert
 * @param p options (@see  FormatHHMMOptions )
 */
export function formatSecondsHHMM(seconds: SECONDS, p?: FormatHHMMOptions): string {
    return formatMinutesHHMM(seconds / SECONDS_IN_ONE_MINUTE, p);
}

/**
 * Formats a number representing a number of seconds into hundredths of hours. Default separator is "."
 * 3960 seconds = 1:06 formatSecondsHundredthsHours(3960) => "1.10"
 * 28080 seconds = 7:48 formatSecondsHundredthsHours(28080, {separator: ","}) => "7,8"
 */
export function formatSecondsHundredthsHours(seconds: SECONDS, p?: { separator: "," | "." }): string {
    const hundredthsHours = (seconds / SECONDS_IN_ONE_HOUR).toFixed(2);
    if (p && p.separator === ",") {
        const splitValues = hundredthsHours.split(".");
        if (splitValues.length === 2) {
            return splitValues[0] + "," + splitValues[1];
        }
    }
    return hundredthsHours;
}

/**
 * Formats a number representing a positive number of minutes into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * Show the actual day if hours > 23.
 * @param timeInMinutes
 * @param options
 * @param options.i18n.dayLabel if not provide the dayLabel is J
 * @deprecated Exported only for tests don't use this function into your project. Use @see formatMinuteOrSecondsHHMMAndChooseOverflow
 * formatMinutesHHMMOverflowPositive(60) => "01:00" or formatMinutesHHMMOverflowPositive(1440) => "00:00 (J+1)"
 */
export function _formatMinutesHHMMOverflowPositive(timeInMinutes: TIME_IN_MINUTES, options?: FormatSecondsHHMMOverflowOptions): string {
    let formattedString = formatMinutesHHMM(timeInMinutes, { dayFormat: true });
    if (options?.displayDaySuffix !== false) {
        const numberDayAfterJDay = Math.floor(minutesToDays(timeInMinutes));
        formattedString += `${numberDayAfterJDay === 0 ? "" : " (" + (options?.i18n?.dayLabel ?? "J") + "+" + numberDayAfterJDay + ")"}`;
    }
    return formattedString;
}

/**
 * Formats a number representing a positive number of seconds into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * Show the actual day if hours > 23.
 * @param seconds
 * @param options
 * @param options.i18n.dayLabel if not provide the dayLabel is J
 * @deprecated Exported only for tests don't use this function into your project. Use @see formatMinuteOrSecondsHHMMAndChooseOverflow
 * formatSecondsHHMMOverflowPositive(3600) => "01:00" or formatSecondsHHMMOverflowPositive(86400) => "00:00 (J+1)"
 */
export function _formatSecondsHHMMOverflowPositive(seconds: SECONDS, options?: FormatSecondsHHMMOverflowOptions): string {
    return _formatMinutesHHMMOverflowPositive(secondsToMinutes(seconds), options);
}

/**
 * Formats a number representing a negative number of minutes into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * Show the actual day if hours < 0.
 * @param timeInMinutes
 * @param options
 * @param options.i18n.dayLabel if not provide the dayLabel is J
 * @deprecated Exported only for tests don't use this function into your project. Use @see formatMinuteOrSecondsHHMMAndChooseOverflow
 * formatMinutesHHMMOverflowNegative(-60) => "23:00 (J - 1)" or formatMinutesHHMMOverflowNegative(-1440) => "00:00 (J-1)"
 */
export function _formatMinutesHHMMOverflowNegative(timeInMinutes: TIME_IN_MINUTES, options?: FormatSecondsHHMMOverflowOptions): string {
    const hours = Math.floor(timeInMinutes / MINUTES_IN_ONE_HOUR);
    const { minutes } = convertTimeInMinutesToDaysHoursMinutes(timeInMinutes);

    const hoursModulo = hours % HOURS_IN_ONE_DAY;
    const str_hours = twoDigits(hoursModulo === 0 ? 0 : HOURS_IN_ONE_DAY + hoursModulo);

    const minsModulo = minutes % MINUTES_IN_ONE_HOUR;
    const str_mins = twoDigits(minsModulo === 0 ? 0 : MINUTES_IN_ONE_HOUR + minsModulo);

    let formattedString = `${str_hours}:${str_mins}`;

    if (options?.displayDaySuffix !== false) {
        const numberDayBeforeJDay = Math.ceil(hours / -HOURS_IN_ONE_DAY);
        formattedString += `${numberDayBeforeJDay === 0 ? "" : " (" + (options?.i18n?.dayLabel ?? "J") + "-" + numberDayBeforeJDay + ")"}`;
    }

    return formattedString;
}

/**
 * Formats a number representing a negative number of seconds into hour:minutesInHour string ensuring hour and minutesInHour have at least 2 digits
 * Show the actual day if hours < 0.
 * @param seconds
 * @param options
 * @param options.i18n.dayLabel if not provide the dayLabel is J
 * @deprecated Exported only for tests don't use this function into your project. Use @see formatMinuteOrSecondsHHMMAndChooseOverflow
 * formatSecondsHHMMOverflowNegative(-3600) => "23:00 (J - 1)" or formatSecondsHHMMOverflowNegative(-86400) => "00:00 (J-1)"
 */
export function _formatSecondsHHMMOverflowNegative(seconds: SECONDS, options?: FormatSecondsHHMMOverflowOptions): string {
    return _formatMinutesHHMMOverflowNegative(secondsToMinutes(seconds), options);
}


/**
 * Choose the good overflow format method to avoid error.
 * Value is a number of minutes or seconds
 * @param value is number of minutes or seconds
 * @param isSeconds
 * @param options
 * @param options.i18n.dayLabel if not provide the dayLabel is J
 */
export function formatMinuteOrSecondsHHMMAndChooseOverflow(value: MINUTES | SECONDS, isSeconds: boolean, options?: FormatSecondsHHMMOverflowOptions): string {
    if (value == null) { return ""; }
    if (value >= 0) {
        return isSeconds ? _formatSecondsHHMMOverflowPositive(value, options) : _formatMinutesHHMMOverflowPositive(value, options);
    }
    return isSeconds ? _formatSecondsHHMMOverflowNegative(value, options) : _formatMinutesHHMMOverflowNegative(value, options);
}

type FormatSecondsHHMMOverflowOptions = { i18n?: { dayLabel: string }, displayDaySuffix?: boolean };

/**
 * Parse the time string a human typed 300 => 03:00, 3.5 => 03:30, 1:1 => 01:01
 * @returns the time in minutes
 */
export function parseHumanTimeString(displayedValue: string, options?: { acceptNegative?: boolean, acceptAboveH24?: boolean }): number | undefined {
    // Regex format to create group
    // aboveH24HumanDuration is for time with 12:00 (J-1) format
    const formatCasesRegex = {
        onlyHour: /^-?(\d{1,2})()$/,
        hourMinute: /^-?(\d{1,4})(\d{2})$/,
        hourColonMinute: /^-?(\d{1,4}):(\d{1,2})$/,
        hourCommaMinute: /^-?(\d{1,4})[,.](\d{1,2})$/,
        aboveH24HumanDuration: /^-?(\d{1,4}):(\d{1,2})\s\(([JD])([+-]\d{1,4})\)$/
    };

    for (const typeFormat of objectKeys(formatCasesRegex)) {
        const regex = new RegExp(formatCasesRegex[typeFormat]);
        const regexValue = regex.exec(displayedValue);
        if (regexValue) {
            const h = Number(regexValue[1]);
            let m = regexValue[2] > "" ? Number(regexValue[2]) : 0;
            if (typeFormat === "hourCommaMinute") {
                m = MINUTES_IN_ONE_HOUR * Number("0." + m);
            }

            // In case of readable format
            let sign = null;
            if (typeFormat === "aboveH24HumanDuration") {
                // Corresponds to the number of day 
                const dayOffset = regexValue[4];
                // Corresponds to the sign - for past and + for future
                sign = dayOffset[0];
                const signValue = sign === "+" ? 1 : -1;
                // Gets the number of days by removing sign
                const value = Number(dayOffset.substring(1));

                m = MINUTES_IN_ONE_DAY * signValue * value + m;
            }

            // Indicates whether days are in the future or in the past 
            // Avoids double negation in case of human format, because less by less gives more 
            const isNegative = sign === "-" ? false : regexValue[0].includes("-");

            if (isNegative && !options?.acceptNegative) {
                continue;
            }

            if (isNegative || options?.acceptAboveH24 || h < HOURS_IN_ONE_DAY && m < MINUTES_IN_ONE_HOUR) {
                return (isNegative ? -1 : 1) * (h * MINUTES_IN_ONE_HOUR + m);
            }
        }
    }
    return undefined;
}

/** Options use to format HHMM times */
export interface FormatHHMMOptions {

    /** 
     * Convert only for 24h "day" duration: 
     * formatSecondsHHMM(91200) => "25:20" / formatMinutesHHMM(91200, {dayFormat: true}) => "01:20" / formatSecondsHHMMSS(91200, {dayFormat: true}) => "01:20:00"
     */
    dayFormat?: boolean;

    /** formatSecondsHHMM(4200, {singleDigitHour: true}) = > 1:10 / formatMinutesHHMM(4200, {singleDigitHour: false}) = > 01:10 */
    singleDigitHour?: boolean;

    /** 
     * Override separator of HHMM formatSecondsHHMM(4200, {separator: "h"}) => 01h10 / formatMinutesHHMM(4200, {separator: ""}) => 0110
     * @default ":"
     */
    separator?: string;
}
